// Custom sound effects for MEGA!
// Using user-uploaded sound files

const SOUND_URLS = {
  friendOnline: 'https://customer-assets.emergentagent.com/job_retro-chat-hub-1/artifacts/btb3f4gr_ON%20LINE%20door-knock.mp3',
  friendOffline: 'https://customer-assets.emergentagent.com/job_retro-chat-hub-1/artifacts/3mnz7iwh_OFF%20LINE%20closing-door.mp3',
  dingDing: 'https://customer-assets.emergentagent.com/job_retro-chat-hub-1/artifacts/oo9hb2cc_DING%20DING.mp3',
  wtf: 'https://customer-assets.emergentagent.com/job_retro-chat-hub-1/artifacts/uj43qjoh_wtf.mp3',
  loginWelcome: 'https://customer-assets.emergentagent.com/job_80904b6f-7286-4784-ad3d-292b160c5a80/artifacts/uestsesn_WELCOME%20TO%20MEGA%20CHAT%20ROBOT.m4a',
  stupidFuk: 'https://customer-assets.emergentagent.com/job_retro-chat-hub-1/artifacts/y2l8g2nx_ROBO%20stupid%20fuk.mp3',
  getItOn: 'https://customer-assets.emergentagent.com/job_retro-chat-hub-1/artifacts/jmh48d7t_ROBO%20get-it-on.mp3'
};

// Cache audio elements for faster playback
const audioCache = {};

// Tracks whether the browser has granted us permission to play audio.
// Browsers block programmatic Audio.play() (e.g. from a WebSocket message)
// until the user has interacted with the page. We "unlock" the cached audio
// elements on the first user gesture so that later sounds (like the room-wide
// "wtf" clip triggered by another user's message) actually play for everyone.
let audioUnlocked = false;

// Preload sounds
const preloadSounds = () => {
  Object.entries(SOUND_URLS).forEach(([key, url]) => {
    if (url) {
      const audio = new Audio(url);
      audio.preload = 'auto';
      audio.crossOrigin = 'anonymous';
      audioCache[key] = audio;
    }
  });
};

// Prime every cached audio element within a user gesture so subsequent
// programmatic playback is permitted by the browser autoplay policy.
const unlockAudio = () => {
  if (audioUnlocked) return;
  audioUnlocked = true;
  Object.values(audioCache).forEach((audio) => {
    try {
      const prevVol = audio.volume;
      audio.muted = true;
      const p = audio.play();
      if (p && p.then) {
        p.then(() => {
          audio.pause();
          audio.currentTime = 0;
          audio.muted = false;
          audio.volume = prevVol;
        }).catch(() => {
          audio.muted = false;
        });
      } else {
        audio.pause();
        audio.currentTime = 0;
        audio.muted = false;
      }
    } catch (_) { /* noop */ }
  });
};

// Initialize preloading + one-time unlock listeners
if (typeof window !== 'undefined') {
  preloadSounds();
  const gestureEvents = ['pointerdown', 'touchstart', 'keydown', 'click'];
  const onGesture = () => {
    unlockAudio();
    gestureEvents.forEach((ev) => window.removeEventListener(ev, onGesture));
  };
  gestureEvents.forEach((ev) => window.addEventListener(ev, onGesture, { passive: true }));
}

// Play a sound effect.
// `volume` is the per-sound default (0..1). The user's notification-volume
// preference (0..100) from /Preferences/ is multiplied on top.
export const playSound = (soundName, volume = 0.7) => {
  // Read preferences synchronously so this stays a one-shot helper
  let prefs = {};
  try {
    prefs = JSON.parse(localStorage.getItem('userPreferences') || '{}');
  } catch (_) { /* noop */ }
  
  // Master enable flag (legacy 'chatSoundsEnabled' or new 'soundEnabled')
  const legacyDisabled = localStorage.getItem('chatSoundsEnabled') === 'false';
  if (legacyDisabled) return;
  if (prefs.soundEnabled === false) return;

  const url = SOUND_URLS[soundName];
  if (!url) {
    console.warn(`Sound "${soundName}" not found or not configured`);
    return;
  }
  
  // Apply the user's notification-volume preference
  const userVol = typeof prefs.notificationVolume === 'number' ? prefs.notificationVolume : 70;
  const effective = Math.max(0, Math.min(1, volume * (userVol / 100)));
  if (effective <= 0.001) return;  // user muted

  try {
    // Clone the primed/cached element so playback permission (granted on the
    // first user gesture) carries over and overlapping triggers can each play.
    const base = audioCache[soundName];
    const audio = base ? base.cloneNode(true) : new Audio(url);
    audio.muted = false;
    audio.volume = effective;
    audio.play().catch(err => {
      console.log('Sound playback blocked:', err.message);
    });
  } catch (error) {
    console.error('Error playing sound:', error);
  }
};

// Sound effect functions for easy use
export const playFriendOnlineSound = () => playSound('friendOnline', 0.6);
export const playFriendOfflineSound = () => playSound('friendOffline', 0.6);
export const playDingDingSound = () => playSound('dingDing', 0.7);
export const playWTFSound = () => playSound('wtf', 0.8);
export const playLoginWelcomeSound = () => playSound('loginWelcome', 0.7);
export const playStupidFukSound = () => playSound('stupidFuk', 0.8);
export const playGetItOnSound = () => playSound('getItOn', 0.8);

// Check if a message contains WTF (case insensitive, as a word)
export const containsWTF = (message) => {
  if (!message) return false;
  return /\bwtf\b/i.test(message);
};

// Check if message contains the middle finger emoticon: ,,l,, (~_~) ,,l,,
export const containsMiddleFinger = (message) => {
  if (!message) return false;
  // Match the pattern ,,l,, (~_~) ,,l,,
  return message.includes(',,l,, (~_~) ,,l,,');
};

// Check if message contains xoxo (case insensitive)
export const containsXOXO = (message) => {
  if (!message) return false;
  return /xoxo/i.test(message);
};

// Check message for sound triggers and play appropriate sound
export const checkAndPlayMessageSounds = (message) => {
  if (!message) return;
  
  if (containsWTF(message)) {
    playWTFSound();
  }
  if (containsMiddleFinger(message)) {
    playStupidFukSound();
  }
  if (containsXOXO(message)) {
    playGetItOnSound();
  }
};

export default {
  playSound,
  playFriendOnlineSound,
  playFriendOfflineSound,
  playDingDingSound,
  playWTFSound,
  playLoginWelcomeSound,
  playStupidFukSound,
  playGetItOnSound,
  containsWTF,
  containsMiddleFinger,
  containsXOXO,
  checkAndPlayMessageSounds,
  SOUND_URLS
};
